export type CardRect = {
  id: string;
  col: number;
  row: number;
  left: number;
  right: number;
  top: number;
  bottom: number;
  centerY: number;
};

type ConnectorLink = {
  id: string;
  fromStepId: string;
  toStepId: string;
};

type ConnectorPath = {
  id: string;
  d: string;
  fromStepId: string;
  toStepId: string;
};

export type ConnectorPoint = [number, number];

export type ConnectorStyle = "tidy" | "loose";

export const cornerRadius = 12;
export const loopbackMargin = 16;
const connectorTrackOffset = 8;
// Loose (curved) mode rounds loopback corners far more than the tidy 12 so the
// orthogonal loop reads as flowy among the curved connectors.
const loopCurveRadius = 40;

type ColumnBounds = {
  left: number;
  right: number;
};

/** Build an SVG path from orthogonal waypoints with rounded corners. */
export function orthogonalConnectorPath(
  pts: ConnectorPoint[],
  r = cornerRadius
): string {
  if (pts.length < 2) return "";
  if (pts.length === 2) {
    return `M ${pts[0]![0]} ${pts[0]![1]} L ${pts[1]![0]} ${pts[1]![1]}`;
  }

  let d = `M ${pts[0]![0]} ${pts[0]![1]}`;

  for (let i = 1; i < pts.length - 1; i++) {
    const [px, py] = pts[i - 1]!;
    const [cx, cy] = pts[i]!;
    const [nx, ny] = pts[i + 1]!;

    const len1 = Math.abs(cx - px) + Math.abs(cy - py);
    const len2 = Math.abs(nx - cx) + Math.abs(ny - cy);
    const actualR = Math.min(r, len1 / 2, len2 / 2);

    if (actualR <= 0) {
      d += ` L ${cx} ${cy}`;
      continue;
    }

    const ux1 = cx === px ? 0 : cx > px ? 1 : -1;
    const uy1 = cy === py ? 0 : cy > py ? 1 : -1;
    const ux2 = nx === cx ? 0 : nx > cx ? 1 : -1;
    const uy2 = ny === cy ? 0 : ny > cy ? 1 : -1;

    d += ` L ${cx - ux1 * actualR} ${cy - uy1 * actualR}`;
    d += ` Q ${cx} ${cy} ${cx + ux2 * actualR} ${cy + uy2 * actualR}`;
  }

  const last = pts[pts.length - 1]!;
  d += ` L ${last[0]} ${last[1]}`;
  return d;
}

/** A flowy cubic bezier from a source's right edge to a target's left edge,
 *  with horizontal control handles — matches the access graph's edges. */
function flowPath(sx: number, sy: number, tx: number, ty: number): string {
  const direction = sx <= tx ? 1 : -1;
  const dx = Math.max(Math.abs(tx - sx) * 0.5, 24);
  return `M ${sx} ${sy} C ${sx + direction * dx} ${sy}, ${tx - direction * dx} ${ty}, ${tx} ${ty}`;
}

export function connectorPath(
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  style: ConnectorStyle,
  trackX = sx + (tx - sx) / 2
): string {
  if (Math.abs(sy - ty) < 1) {
    return orthogonalConnectorPath(
      [
        [sx, sy],
        [tx, ty],
      ],
      cornerRadius
    );
  }
  if (style === "loose") return flowPath(sx, sy, tx, ty);
  return orthogonalConnectorPath(
    [
      [sx, sy],
      [trackX, sy],
      [trackX, ty],
      [tx, ty],
    ],
    cornerRadius
  );
}

function getColumnBounds(cards: Map<string, CardRect>) {
  const bounds = new Map<number, ColumnBounds>();

  for (const card of cards.values()) {
    const existing = bounds.get(card.col);
    if (existing) {
      existing.left = Math.min(existing.left, card.left);
      existing.right = Math.max(existing.right, card.right);
      continue;
    }
    bounds.set(card.col, { left: card.left, right: card.right });
  }

  return bounds;
}

function getGapTrackX(
  columnBounds: Map<number, ColumnBounds>,
  leftCol: number,
  rightCol: number
) {
  const left = columnBounds.get(leftCol);
  const right = columnBounds.get(rightCol);
  if (!left || !right) return null;
  return left.right + (right.left - left.right) / 2;
}

/**
 * Routes orthogonal connectors between workflow cards.
 *
 * All paths use only horizontal and vertical segments with rounded
 * corners. Forward links route through a shared target-side spine so
 * sibling verticals line up; loopback links route via a channel above
 * or below the grid.
 *
 * In curved mode (the roomy/spacious layout) forward links instead draw as
 * flowy cubic beziers straight to their target, so branches fan apart on
 * their own curves rather than stacking on one rigid spine.
 */
export function routeConnectors(
  cards: Map<string, CardRect>,
  links: ConnectorLink[],
  curved = false
): ConnectorPath[] {
  if (links.length === 0 || cards.size === 0) return [];

  const paths: ConnectorPath[] = [];

  // Group links by source step
  const linksBySource = new Map<string, ConnectorLink[]>();
  for (const link of links) {
    const group = linksBySource.get(link.fromStepId);
    if (group) {
      group.push(link);
    } else {
      linksBySource.set(link.fromStepId, [link]);
    }
  }

  // Compute grid bounds for loopback channel positioning
  let gridTop = Infinity;
  let gridBottom = -Infinity;
  for (const card of cards.values()) {
    if (card.top < gridTop) gridTop = card.top;
    if (card.bottom > gridBottom) gridBottom = card.bottom;
  }
  const columnBounds = getColumnBounds(cards);

  for (const [, outgoing] of linksBySource) {
    const source = cards.get(outgoing[0]!.fromStepId);
    if (!source) continue;

    const resolved = outgoing
      .map((link) => ({ link, card: cards.get(link.toStepId) }))
      .filter(
        (t): t is { link: ConnectorLink; card: CardRect } => t.card != null
      );

    const forward = resolved.filter((t) => t.card.col > source.col);
    const backward = resolved.filter((t) => t.card.col <= source.col);

    // --- Forward routing ---
    if (forward.length > 0) {
      const nearestCol = Math.min(...forward.map((t) => t.card.col));
      const nearestLeft = Math.min(...forward.map((t) => t.card.left));
      // Place the spine in the gap immediately to the right of the source
      // column so the vertical segment never passes through intermediate cards.
      const spineGapRight = columnBounds.has(source.col + 1)
        ? source.col + 1
        : nearestCol;
      const gapMid =
        getGapTrackX(columnBounds, source.col, spineGapRight) ??
        source.right + (nearestLeft - source.right) / 2;

      for (const { link, card: target } of forward) {
        let d: string;
        if (Math.abs(source.centerY - target.centerY) < 1) {
          // Same row: a straight horizontal line in either mode.
          d = connectorPath(
            source.right,
            source.centerY,
            target.left,
            target.centerY,
            curved ? "loose" : "tidy"
          );
        } else if (curved) {
          // Flowy: one bezier straight to the target, so branches fan apart
          // on their own curves.
          d = connectorPath(
            source.right,
            source.centerY,
            target.left,
            target.centerY,
            "loose"
          );
        } else {
          // Rigid: a Z-route through the shared midline spine.
          d = connectorPath(
            source.right,
            source.centerY,
            target.left,
            target.centerY,
            "tidy",
            gapMid
          );
        }
        paths.push({
          id: link.id,
          fromStepId: link.fromStepId,
          toStepId: link.toStepId,
          d,
        });
      }
    }

    // --- Backward / loopback routing ---
    for (const { link, card: target } of backward) {
      const routeAbove = source.row <= target.row;

      const exitX =
        getGapTrackX(columnBounds, source.col, source.col + 1) ??
        source.right + connectorTrackOffset;
      const entryX =
        getGapTrackX(columnBounds, target.col - 1, target.col) ??
        target.left - connectorTrackOffset;

      // The horizontal run only passes under the cards between its two tracks,
      // so hug just those for a tight loop instead of dropping below the whole
      // grid (which pushes the loop far past cards it never crosses).
      const runLeft = Math.min(exitX, entryX);
      const runRight = Math.max(exitX, entryX);
      let spanTop = Infinity;
      let spanBottom = -Infinity;
      for (const c of cards.values()) {
        if (c.right < runLeft || c.left > runRight) continue;
        if (c.top < spanTop) spanTop = c.top;
        if (c.bottom > spanBottom) spanBottom = c.bottom;
      }
      const channelY = routeAbove
        ? (spanTop === Infinity ? gridTop : spanTop) - loopbackMargin
        : (spanBottom === -Infinity ? gridBottom : spanBottom) + loopbackMargin;

      paths.push({
        id: link.id,
        fromStepId: link.fromStepId,
        toStepId: link.toStepId,
        d: orthogonalConnectorPath(
          [
            [source.right, source.centerY],
            [exitX, source.centerY],
            [exitX, channelY],
            [entryX, channelY],
            [entryX, target.centerY],
            [target.left, target.centerY],
          ],
          curved ? loopCurveRadius : cornerRadius
        ),
      });
    }
  }

  return paths;
}
